import styled from "@emotion/styled"
import { useTranslation } from "../TranslationProvider"
import { useEffect, useRef, useState } from "react"
import FullscreenReviewViewer from "./FullscreenReviewViewer"

type IReview = {
  date: string
  name: string
  title: string
  text: string
  rating: number
}

const totalReviews = 41
const reviewRefreshRate = 6_000

// Function to calculate text length based on viewport width
const getTextLengthForViewport = (width: number): number => {
  if (width < 500) return 180
  if (width < 768) return 200
  if (width < 1024) return 250
  return 300
}

export default function Review() {
  const { t } = useTranslation()

  const [reviewIdx, setReviewIdx] = useState(
    Math.ceil(Math.random() * totalReviews)
  )

  const [fadeState, setFadeState] = useState<"fade-in" | "fade-out">("fade-in")
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)
  const [reviewTextLength, setReviewTextLength] = useState(
    getTextLengthForViewport(window.innerWidth)
  )

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Only start the interval if fullscreen is not open
    if (!isFullscreenOpen) {
      intervalRef.current = setInterval(() => {
        setFadeState("fade-out")

        timeoutRef.current = setTimeout(() => {
          setReviewIdx((prevIdx) => {
            let nextIdx = Math.ceil(Math.random() * totalReviews)

            if (nextIdx === prevIdx) {
              nextIdx = nextIdx === totalReviews ? 1 : nextIdx + 1
            }

            return nextIdx
          })

          setFadeState("fade-in")
        }, 700)
      }, reviewRefreshRate)
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [isFullscreenOpen]) // Depend on isFullscreenOpen to restart/stop timer

  // Handle window resize to update text length dynamically
  useEffect(() => {
    const handleResize = () => {
      setReviewTextLength(getTextLengthForViewport(window.innerWidth))
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  const openFullscreen = () => {
    setIsFullscreenOpen(true)
  }

  const closeFullscreen = () => {
    setIsFullscreenOpen(false)

    // Immediately load the next review when closing fullscreen
    setFadeState("fade-out")
    setTimeout(() => {
      setReviewIdx((prevIdx) => {
        let nextIdx = Math.ceil(Math.random() * totalReviews)

        if (nextIdx === prevIdx) {
          nextIdx = nextIdx === totalReviews ? 1 : nextIdx + 1
        }

        return nextIdx
      })

      setFadeState("fade-in")
    }, 700)
  }

  const navigateToNextReview = () => {
    setReviewIdx((prevIdx) => (prevIdx === totalReviews ? 1 : prevIdx + 1))
  }

  const navigateToPrevReview = () => {
    setReviewIdx((prevIdx) => (prevIdx === 1 ? totalReviews : prevIdx - 1))
  }

  function getReview(reviewIdx: number): IReview {
    const review = t(`reviews.review${reviewIdx}`, {
      returnObjects: true,
    }) as IReview

    return review
  }

  const currentReview = getReview(reviewIdx)
  const { date, name, title, text, rating } = currentReview

  const truncatedText =
    text.length > reviewTextLength
      ? text.slice(0, reviewTextLength - 3) + "..."
      : text

  const ratingArray = [
    ...Array.from({ length: Number(rating) }, (_, idx) => (
      <div key={`blueStar${idx}`}>{blueStar}</div>
    )),
    ...Array.from({ length: 5 - Number(rating) }, (_, idx) => (
      <div key={`grayStar${idx}`}>{grayStar}</div>
    )),
  ]

  return (
    <>
      <ReviewCardWrapper
        className="reveal"
        onClick={openFullscreen}
        title={t("accessibility.clickToViewFullscreen")}>
        <ReviewCard className={fadeState}>
          <ReviewHeader>
            <ReviewDate>{date}</ReviewDate>

            <ReviewRating>{ratingArray}</ReviewRating>
          </ReviewHeader>

          <ReviewBody>
            <ReviewTitle>{title}</ReviewTitle>

            <ReviewText>{truncatedText}</ReviewText>

            <ReviewName>- {name}</ReviewName>
          </ReviewBody>
        </ReviewCard>
        <ClickMe>{t("app.clickMe")}</ClickMe>
      </ReviewCardWrapper>

      <FullscreenReviewViewer
        review={currentReview}
        reviewIndex={reviewIdx}
        totalReviews={totalReviews}
        isOpen={isFullscreenOpen}
        onClose={closeFullscreen}
        onNext={navigateToNextReview}
        onPrev={navigateToPrevReview}
      />
    </>
  )
}

const ReviewCardWrapper = styled.div`
  width: 100%;
  min-height: 14rem;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  cursor: pointer;
`

const ReviewCard = styled.div`
  max-width: 48rem;
  min-height: 10rem;
  padding: 1rem;
  border-radius: 0.3rem;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1rem;

  background-color: var(--white);
  user-select: none;

  font-family: "Questrial", sans-serif;

  opacity: 1;
  transition: opacity 0.7s ease-in-out, transform 0.3s ease,
    box-shadow 0.3s ease;

  &.fade-out {
    opacity: 0;
  }

  &.fade-in {
    opacity: 1;
  }

  &:focus {
    outline: 2px solid var(--special);
    outline-offset: 4px;
  }
`

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`

const ReviewDate = styled.div`
  color: darkgray;
  font-size: 16px;
`

const ReviewRating = styled.div`
  width: 10rem;

  display: flex;
  justify-content: flex-end;

  svg {
    max-height: 1.7rem;
  }
`

const ReviewBody = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  color: black;
  font-size: 18px;
`

const ReviewTitle = styled.h3`
  margin: 0;
  text-align: left;
  font-size: 16px;

  @media screen and (min-width: 500px) {
    font-size: 18px;
  }
`

const ReviewText = styled.div`
  text-align: left;
  font-size: 14px;

  @media screen and (min-width: 500px) {
    font-size: 16px;
  }
`

const ReviewName = styled.div`
  justify-self: flex-end;
  text-align: right;
  font-size: 16px;

  @media screen and (min-width: 500px) {
    font-size: 18px;
  }
`

const ClickMe = styled.div`
  color: var(--white);
  font-size: 1rem;
  user-select: none;
`

const blueStar = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="rebeccapurple">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.86L12 17.77l-6.18 3.23L7 14.14l-5-4.87 6.91-1.01L12 2z" />
  </svg>
)

const grayStar = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="lightgray">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.86L12 17.77l-6.18 3.23L7 14.14l-5-4.87 6.91-1.01L12 2z" />
  </svg>
)
